import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowClockwise, CaretLeft, SpinnerGap, Trash, WarningCircle } from '@phosphor-icons/react';
import { BestaetigenDialog, ImmoButton, SchwebenderToast, useToastPill } from '../../../../components/immobilien/ui';
import { useTelefon } from '../../../../components/immobilien/ui/GlassModal';
import { ueberlagerungOffen } from '../../../../components/immobilien/ui/ueberlagerung';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import { kurzDatum, standText } from '../../../../lib/zaehler/liste';
import { useZaehlerDetail, type AblesungZeile } from '../../../../lib/zaehler/useZaehlerDetail';
import { entferneAblesungFoto, loescheAblesung, loescheZaehler, setzeAblesungFoto, setzeAktiv } from '../../../../lib/zaehler/zaehlerApi';
import { FotoAnsicht } from './FotoAnsicht';
import { meldeZaehlerGeaendert } from '../../../../lib/zaehler/useZaehlerListe';
import { KopfKarte } from './KopfKarte';
import { AblesungenKarte, NotizenKarte, StammdatenKarte, VerlaufKarte, WerteKarte } from './Karten';
import ZaehlerFormular from '../ZaehlerFormular';
import AblesungFormular from '../AblesungFormular';

/** Toast, den die Liste nach dem Zurückkehren zeigt (z. B. „Zähler gelöscht“). */
export interface ListenToast { text: string; fehler?: boolean }

/**
 * Detailseite eines Zählers (Handoff Zähler §5.3) – ersetzt die frühere Sidebar. „Alle Zähler“ und Esc führen
 * zur Liste zurück; die Listen-Parameter (Suche, Filter, Sortierung) stehen in der URL und bleiben erhalten.
 */
export default function ZaehlerSeite({ zaehlerId, userProfile }: { zaehlerId: string; userProfile?: { id?: string } | null }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { mobil } = useMobilModus();
  const telefon = useTelefon();
  const { zustand, neuLaden } = useZaehlerDetail(zaehlerId);
  const [toast, zeige] = useToastPill();
  const [schaltet, setSchaltet] = useState(false);
  const [loeschFrage, setLoeschFrage] = useState(false);
  const [loescht, setLoescht] = useState(false);
  const [ablesungWeg, setAblesungWeg] = useState<AblesungZeile | null>(null);
  // #512: Foto ansehen / hinzufügen / ersetzen / entfernen
  const [fotoAblesung, setFotoAblesung] = useState<AblesungZeile | null>(null);
  const [fotoLaedt, setFotoLaedt] = useState<string | null>(null);
  const [ablesungLoescht, setAblesungLoescht] = useState(false);
  const [formular, setFormular] = useState<'zaehler' | 'ablesung' | null>(null);

  const zurListe = useCallback((listenToast?: ListenToast) => {
    navigate(`/admin/zaehler${search}`, listenToast ? { state: { toast: listenToast } } : undefined);
  }, [navigate, search]);

  // Esc: zurück zur Liste – außer ein Dialog, Menü oder Formular ist offen (die schließen selbst)
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || ueberlagerungOffen() || formular || ablesungWeg) return;
      if (loeschFrage) { setLoeschFrage(false); return; }
      zurListe();
    };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [zurListe, formular, ablesungWeg, loeschFrage]);

  // unbekannte ID (gelöscht, falscher Link): zurück zur Liste wie bisher
  useEffect(() => {
    if (zustand.status === 'fehlt') navigate(`/admin/zaehler${search}`, { replace: true });
  }, [zustand.status, navigate, search]);

  const daten = zustand.status === 'ok' ? zustand : null;

  const aktivUmschalten = async () => {
    if (!daten) return;
    const neu = !daten.zaehler.aktiv;
    setSchaltet(true);
    try {
      await setzeAktiv(daten.zaehler.id, neu);
      zeige(neu ? 'Zähler aktiviert' : 'Zähler deaktiviert');
      meldeZaehlerGeaendert();
      neuLaden();
    } catch (e) {
      zeige(`Fehler: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`, true);
    } finally {
      setSchaltet(false);
    }
  };

  const zaehlerLoeschen = async () => {
    if (!daten) return;
    setLoescht(true);
    try {
      const { fotosEntfernt } = await loescheZaehler(daten.zaehler.id, daten.ablesungen.map(a => a.foto_url));
      meldeZaehlerGeaendert();
      zurListe(fotosEntfernt === false
        ? { text: 'Zähler gelöscht – Fotos konnten nicht aus dem Speicher entfernt werden', fehler: true }
        : { text: 'Zähler gelöscht' });
    } catch (e) {
      zeige(`Fehler beim Löschen: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`, true);
      setLoescht(false);
    }
  };

  const ablesungLoeschen = async () => {
    if (!daten || !ablesungWeg) return;
    setAblesungLoescht(true);
    try {
      const r = await loescheAblesung(ablesungWeg, daten.ablesungen);
      setAblesungWeg(null);
      if (r.korrekturFehler) zeige(`Ablesung gelöscht – Verbrauch der folgenden Ablesung nicht angepasst (${r.korrekturFehler})`, true);
      else if (r.fotoEntfernt === false) zeige('Ablesung gelöscht – Foto konnte nicht aus dem Speicher entfernt werden', true);
      else zeige('Ablesung gelöscht');
      meldeZaehlerGeaendert();
      neuLaden();
    } catch (e) {
      zeige(`Fehler beim Löschen: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`, true);
    } finally {
      setAblesungLoescht(false);
    }
  };

  const fotoSetzen = async (a: AblesungZeile, datei: File) => {
    if (!daten) return;
    setFotoLaedt(a.id);
    try {
      const r = await setzeAblesungFoto(daten.zaehler.id, a, datei, daten.ablesungen);
      zeige(a.foto_url ? (r.altesEntfernt === false ? 'Foto ersetzt – altes Foto blieb im Speicher' : 'Foto ersetzt') : 'Foto hinzugefügt', r.altesEntfernt === false);
      setFotoAblesung(null);
      meldeZaehlerGeaendert();
      neuLaden();
    } catch (e) {
      zeige(e instanceof Error ? e.message : 'Foto konnte nicht gespeichert werden', true);
    } finally {
      setFotoLaedt(null);
    }
  };

  const fotoEntfernen = async (a: AblesungZeile) => {
    if (!daten) return;
    setFotoLaedt(a.id);
    try {
      const r = await entferneAblesungFoto(a, daten.ablesungen);
      zeige(r.dateiEntfernt === false ? 'Foto entfernt – Datei blieb im Speicher' : 'Foto entfernt', r.dateiEntfernt === false);
      setFotoAblesung(null);
      neuLaden();
    } catch (e) {
      zeige(e instanceof Error ? e.message : 'Foto konnte nicht entfernt werden', true);
    } finally {
      setFotoLaedt(null);
    }
  };

  return (
    <>
      <div className="flex items-center flex-wrap min-w-0" style={{ gap: 12 }}>
        <ImmoButton art="glas" groesse={mobil ? 44 : 36} icon={CaretLeft} onClick={() => zurListe()}
          style={{ height: mobil ? 44 : 38, padding: '0 14px 0 10px', fontSize: 13, fontWeight: 700 }}>
          Alle Zähler
        </ImmoButton>
        <span className="truncate min-w-0" style={{ fontSize: 13, color: 'var(--ink3)' }}>
          Zähler / {daten ? daten.zaehler.zaehler_nummer : '…'}
        </span>
      </div>

      {zustand.status === 'laedt' && (
        <div aria-busy="true" className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: '60px 20px', gap: 10 }}>
          <SpinnerGap size={26} color="var(--acc)" className="animate-spin" />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink3)' }}>Lade Zähler...</span>
        </div>
      )}

      {zustand.status === 'fehler' && (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 26, padding: 20, gap: 14 }}>
          <WarningCircle weight="fill" size={28} color="var(--bad)" />
          <div className="flex flex-col flex-1" style={{ minWidth: 200 }}>
            <span style={{ fontSize: 15, fontWeight: 800 }}>Zähler konnte nicht geladen werden</span>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{zustand.meldung}</span>
          </div>
          <ImmoButton icon={ArrowClockwise} onClick={neuLaden}>Erneut versuchen</ImmoButton>
        </div>
      )}

      {daten && (
        <>
          <KopfKarte z={daten.zaehler} letzte={daten.ablesungen[0] ?? null} telefon={telefon} mobil={mobil} schaltet={schaltet}
            loeschFrage={loeschFrage} loescht={loescht} onAktiv={() => { void aktivUmschalten(); }} onBearbeiten={() => setFormular('zaehler')}
            onLoeschFrage={setLoeschFrage} onLoeschen={() => { void zaehlerLoeschen(); }} />
          <div className="flex flex-wrap items-start" style={{ gap: 14 }}>
            <div className="flex flex-col min-w-0" style={{ flex: '1 1 340px', gap: 14 }}>
              <StammdatenKarte z={daten.zaehler} />
              <WerteKarte z={daten.zaehler} ablesungen={daten.ablesungen} />
            </div>
            <div className="flex flex-col min-w-0" style={{ flex: '1.4 1 420px', gap: 14 }}>
              <AblesungenKarte z={daten.zaehler} ablesungen={daten.ablesungen} mobil={mobil}
                onNeu={() => setFormular('ablesung')} onLoeschen={setAblesungWeg}
                onFoto={setFotoAblesung} onFotoNeu={(a, f) => { void fotoSetzen(a, f); }} fotoLaedt={fotoLaedt} />
              <VerlaufKarte z={daten.zaehler} ablesungen={daten.ablesungen} />
              <NotizenKarte text={daten.zaehler.notizen} />
            </div>
          </div>
        </>
      )}

      <SchwebenderToast toast={toast} mobil={mobil} />

      {fotoAblesung && daten && (
        <FotoAnsicht z={daten.zaehler} ablesung={fotoAblesung} laeuft={fotoLaedt === fotoAblesung.id} onSchliessen={() => setFotoAblesung(null)}
          onErsetzen={f => { void fotoSetzen(fotoAblesung, f); }} onEntfernen={() => { void fotoEntfernen(fotoAblesung); }} />
      )}
      {daten && ablesungWeg && (
        <BestaetigenDialog titel="Ablesung löschen?" icon={Trash} bestaetigenLabel="Löschen" laeuft={ablesungLoescht}
          text={`Die Ablesung vom ${kurzDatum(ablesungWeg.datum)} wird gelöscht. Der Verbrauch der folgenden Ablesung wird neu berechnet.`}
          detail={`${standText(ablesungWeg.wert, daten.zaehler.einheit)}${ablesungWeg.foto_url ? ' · mit Foto' : ''}`}
          onBestaetigen={() => { void ablesungLoeschen(); }} onAbbrechen={() => setAblesungWeg(null)} />
      )}
      {daten && formular === 'zaehler' && (
        <ZaehlerFormular zaehler={daten.zaehler} onSchliessen={() => setFormular(null)}
          onGespeichert={m => { setFormular(null); zeige(m); meldeZaehlerGeaendert(); neuLaden(); }} />
      )}
      {daten && formular === 'ablesung' && (
        <AblesungFormular zaehler={daten.zaehler} ablesungen={daten.ablesungen} nutzerId={userProfile?.id ?? null}
          onSchliessen={() => setFormular(null)}
          onGespeichert={(m, fehler) => { setFormular(null); zeige(m, fehler); meldeZaehlerGeaendert(); neuLaden(); }} />
      )}
    </>
  );
}
